// @ts-expect-error Missing types
import jsreport from '@jsreport/browser-client';
import { type SILogReport } from '@/reports/models/report';
import { type ReportServerConfig } from '@/reports/models/report';
import DateRangeFilter from '../../shared/DateRangeFilter.vue';
import { ReportType } from 'shared/reports/domain/reportType';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useHttpClient } from '@/common/api/httpClient';
import { useDarkMode } from 'shared/themes/composables/darkMode';
import { useThemeStore } from 'shared/themes/stores/themeStore';

interface ReportData {
  darkMode: boolean;
  tiYardTheme: boolean;
  sumSortedNumberOfLogs: number;
  sumSortedVolume: string;
  sumConsumedNumberOfLogs: number;
  sumConsumedVolume: string;
  sumDiffNumberOfLogs: number;
  sumDiffVolume: string;
  products: ReportDataProduct[];
}

interface ReportDataProduct {
  productName: string;
  sortedNumberOfLogs: number;
  sortedVolume: string;
  consumedNumberOfLogs: number;
  consumedVolume: string;
  diffNumberOfLogs: number;
  diffVolume: string;
}

interface LogYardPeriodData {
  productName: string;
  sortedNumberOfLogs: number;
  sortedVolume: number;
  consumedNumberOfLogs: number;
  consumedVolume: number;
  diffNumberOfLogs: number;
  diffVolume: number;
}

interface LogYardPeriodRequest {
  from: string;
  to: string;
}

const REPORT_TYPE = ReportType.LogYardPeriod;
const TEMPLATE_NAME = 'log_yard_period';

export function usePeriodReport(): SILogReport {
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  async function fetchDataFromServer(config: ReportServerConfig): Promise<ReportData | null> {
    const httpClient = useHttpClient();
    const headers = httpClient.defaultHeaders();

    // Get date range from filter values or use default
    const filterValues = config.filterValues as { from: string; to: string } | undefined;
    if (!filterValues?.from || !filterValues?.to) {
      throw new Error('Filter values "from" and "to" are required');
    }

    const body: LogYardPeriodRequest = {
      from: filterValues.from,
      to: filterValues.to,
    };

    const request = new Request(httpClient.siteUrl(`report/${REPORT_TYPE}`), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const response = await httpClient.execute(request);

    if (response.ok) {
      const reportDataFromServer = (await response.json()) as LogYardPeriodData[];

      jsreport.serverUrl = config.reportUrl;
      if (config.authorization) {
        jsreport.headers['Authorization'] = config.authorization;
      }

      const darkMode = useDarkMode();
      const { enabled } = storeToRefs(darkMode);
      const themeStore = useThemeStore();
      const theme = themeStore.currentTheme;

      const reportData: ReportData = {
        darkMode: enabled.value,
        tiYardTheme: theme === "TiYard",
        sumSortedNumberOfLogs: 0,
        sumSortedVolume: '0.0',
        sumConsumedNumberOfLogs: 0,
        sumConsumedVolume: '0.0',
        sumDiffNumberOfLogs: 0,
        sumDiffVolume: '0.0',
        products: [],
      };

      let sortedLogs = 0;
      let sortedVolume = 0.0;
      let consumedLogs = 0;
      let consumedVolume = 0.0;
      let diffLogs = 0;
      let diffVolume = 0.0;
      for (const product of reportDataFromServer) {
        sortedLogs += product.sortedNumberOfLogs;
        sortedVolume += product.sortedVolume;
        consumedLogs += product.consumedNumberOfLogs;
        consumedVolume += product.consumedVolume;
        diffLogs += product.diffNumberOfLogs;
        diffVolume += product.diffVolume;

        reportData.products.push({
          productName: product.productName,
          sortedNumberOfLogs: product.sortedNumberOfLogs,
          sortedVolume: product.sortedVolume.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
          consumedNumberOfLogs: product.consumedNumberOfLogs,
          consumedVolume: product.consumedVolume.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
          diffNumberOfLogs: product.diffNumberOfLogs,
          diffVolume: product.diffVolume.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
        });
      }

      reportData.sumSortedNumberOfLogs = sortedLogs;
      reportData.sumSortedVolume = sortedVolume.toLocaleString(languageStore.language.code, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
      reportData.sumConsumedNumberOfLogs = consumedLogs;
      reportData.sumConsumedVolume = consumedVolume.toLocaleString(languageStore.language.code, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
      reportData.sumDiffNumberOfLogs = diffLogs;
      reportData.sumDiffVolume = diffVolume.toLocaleString(languageStore.language.code, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

      reportData.products.sort((a, b) =>
        a.productName.localeCompare(b.productName, languageStore.language.code, {
          numeric: true,
          sensitivity: 'base',
        }),
      );

      return reportData;
    }

    return null;
  }

  return {
    name: translations.value.reports.reportName(REPORT_TYPE),
    type: REPORT_TYPE,

    exportFormats: [
      {
        label: translations.value.reports.exportFormat.pdf,
        icon: 'pi pi-file-pdf',
        format: 'pdf',
      },
      {
        label: translations.value.reports.exportFormat.excel,
        icon: 'pi pi-file-excel',
        format: 'excel',
      },
    ],

    filterComponent: DateRangeFilter,

    async showReport(config: ReportServerConfig | null): Promise<string> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: 'html',
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code } },
          });

          let url = await report.toDataURI();
          url = url.replace(';base64,', ';charset=utf-8;base64,');
          return url;
        } catch (error) {
          console.error('Error generating report:', error);
          return '';
        }
      }

      return '';
    },

    async exportReport(config: ReportServerConfig | null, format: string): Promise<boolean> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      let recipe = 'html';
      let extension = '.txt';
      if (format === 'pdf') {
        recipe = 'chrome-pdf';
        extension = 'pdf';
      } else if (format === 'excel') {
        recipe = 'html-to-xlsx';
        extension = 'xlsx';
      }

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: recipe,
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code },  },
          });

          report.download(`${TEMPLATE_NAME}.${extension}`);
          return true;
        } catch (error) {
          console.error('Error exporting report:', error);
        }
      }

      return false;
    },
  };
}
